// The key-refresh stepper (docs/08-app-design.md 5.8), one component reused
// by Restore, by the expired-marker case, by the burned-key case and by
// "Refresh now" on the Keys page. Runs `engine.refreshKey` and shows its
// progress; copy names the reason it is running. A refresh refused because
// the fee reserve cannot pay (a RefreshFailed caused by InsufficientGas)
// points to this device's older reserve when it can be moved
// (docs/18-one-fee-reserve.md section 6).

import { useEffect, useRef, useState } from 'react';
import type { VaultSigner } from '@thevault/sdk';
import { type Engine, InsufficientGas, type OlderReserve, RefreshFailed, type RefreshProgress } from '../engine';
import { LOCKED_WHILE_WAITING } from './Ceremony';
import { type OlderReserveHandle, OlderReservePointer } from './OlderReserve';
import { useSession } from '../session';
import { explorerBase, explorerUrl, shortSig } from '../util';
import { Button, StatusLine, type Step, Stepper } from './primitives';

/** The failed step's status word, by what kind of failure it was (never "Did not land.": a refused refresh was not sent, and a landed-and-failed refresh is distinct from either). */
const FAILED_WORDS: Record<'rejected' | 'failed' | 'unknown', string> = { rejected: 'Not sent.', failed: 'Failed on chain.', unknown: 'Outcome unknown.' };

export function KeyRefresh({
  engine,
  signer,
  reason,
  autoStart = true,
  onDone,
  onProgress,
  older,
}: {
  engine: Engine;
  signer: VaultSigner;
  reason: string;
  autoStart?: boolean;
  onDone?: (signature: string) => void;
  /** Every progress event of every attempt, also after this stepper has unmounted (Restore keeps them for its saved screen after a lock). */
  onProgress?: (p: RefreshProgress) => void;
  /** The page's older reserve handle when the page shows that notice itself (the overview): a refusal's reading is adopted into it, and the pointer links on the page. */
  older?: OlderReserveHandle;
}) {
  const { settings } = useSession();
  const [progress, setProgress] = useState<RefreshProgress | null>(null);
  // The older reserve a fee reserve refusal read (InsufficientGas.movable), for the pointer under the failed step.
  const [movable, setMovable] = useState<OlderReserve | null>(null);
  const started = useRef(false);
  // The tree this device held when the stepper mounted: if a later "check again" finds it advanced, the refresh landed after all (R45).
  const initialTreeIndex = useRef(signer.treeIndex);
  const run = async () => {
    started.current = true;
    setMovable(null);
    try {
      await engine.refreshKey(signer, (p) => {
        setProgress(p);
        onProgress?.(p);
      });
    } catch (e) {
      // progress carries the failure; a fee reserve refusal also carries the older reserve it read
      const reading = e instanceof RefreshFailed && e.cause instanceof InsufficientGas ? e.cause.movable : null;
      setMovable(reading);
      if (reading) older?.adopt(reading);
    }
  };
  useEffect(() => {
    if (autoStart && !started.current) void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);
  useEffect(() => {
    if (progress?.step === 'landed' && progress.signature) onDone?.(progress.signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress?.step]);

  /**
   * Re-read the chain for the two follow-ups below, or say why it could not be read and return false. The outcome
   * line keeps its step: a failed read says nothing about the refresh. Once the app has locked the engine refuses with
   * "locked" (R68): the lock sentence. No screen leaves this stepper up over a locked session any more (Restore swaps
   * its refresh step for the saved screen at the lock), but a re-read already in flight at the lock still fails here,
   * caught, after the stepper has unmounted.
   */
  const recheck = async (): Promise<boolean> => {
    try {
      await engine.sync();
      return true;
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      const error = raw === 'locked' ? LOCKED_WHILE_WAITING : `The chain could not be read to check the outcome. Details: ${raw}`;
      setProgress((p) => (p ? { ...p, error } : p));
      return false;
    }
  };

  const checkAgain = async () => {
    if (!(await recheck())) return;
    if (signer.treeIndex > initialTreeIndex.current && progress?.signature) onDone?.(progress.signature);
  };

  /**
   * The recovery message is deterministic (docs/08 5.8, R55): a refresh
   * dropped before the network received it can simply be re-sent. Sync
   * first in case the earlier attempt actually landed after all - if the
   * tree already advanced past what this stepper mounted with, that is
   * reported as the same landed outcome `checkAgain` would find, rather
   * than resending a recovery the vault has already retired the key for.
   */
  const tryAgain = async () => {
    if (!(await recheck())) return;
    if (signer.treeIndex > initialTreeIndex.current) {
      if (progress?.signature) onDone?.(progress.signature);
      return;
    }
    await run();
  };

  const step = progress?.step ?? 'idle';
  const state = (s: 'preparing' | 'sending' | 'landed'): Step['state'] => {
    const order = ['preparing', 'sending', 'landed'] as const;
    if (step === 'failed' || step === 'unknown') return order.indexOf(s) <= failedAt(progress) ? 'failed' : 'todo';
    if (step === 'idle') return 'todo';
    const cur = order.indexOf(step as (typeof order)[number]);
    const mine = order.indexOf(s);
    return mine < cur ? 'done' : mine === cur ? (s === 'landed' ? 'done' : 'active') : 'todo';
  };
  const txBase = explorerBase(settings.rpcUrl, 'tx');
  return (
    <div className="stack">
      <p className="ink">{reason}</p>
      <Stepper
        steps={[
          { title: 'Preparing the next key', detail: 'The vault derives its next key tree inside this device, about a second.', state: state('preparing') },
          { title: 'Sending', detail: 'One small transaction, paid from the fee reserve. No rent.', state: state('sending') },
          { title: 'Landed', detail: progress?.signature ? <>Transaction {explorerUrl(txBase, progress.signature) ? <a href={explorerUrl(txBase, progress.signature)!} target="_blank" rel="noreferrer">{shortSig(progress.signature)} ↗</a> : shortSig(progress.signature)}. The previous key is retired.</> : undefined, state: state('landed') },
        ]}
      />
      {step === 'failed' ? (
        <>
          <StatusLine state="failed" word={progress?.kind ? FAILED_WORDS[progress.kind] : 'Not sent.'}>
            {progress?.error}
          </StatusLine>
          {movable ? <OlderReservePointer reading={movable} here={!!older} /> : null}
          <div className="actions">
            <Button onClick={() => void run()}>Try again</Button>
          </div>
        </>
      ) : null}
      {step === 'unknown' ? (
        <>
          <StatusLine state="neutral" word="Outcome unknown.">
            {progress?.error}
          </StatusLine>
          <div className="actions">
            <Button onClick={() => void checkAgain()}>Check again</Button>
            <Button variant="ghost" onClick={() => void tryAgain()}>
              Try again
            </Button>
          </div>
        </>
      ) : null}
      {step === 'idle' && !autoStart ? (
        <div className="actions">
          <Button onClick={() => void run()}>Refresh now</Button>
        </div>
      ) : null}
    </div>
  );
}

function failedAt(p: RefreshProgress | null): number {
  // A failure reported before 'sending' began means preparing failed; after it, sending failed.
  return p?.signature ? 2 : 1;
}
